import Head from 'next/head';
import type { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '../_app';
import React from 'react';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import Layout from '@/layouts';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { fetchLoadingDataWith } from '@/lib/service';
import { page3JsonLd } from '@/lib/jsonld';

const PageMainContent = dynamic(() => import('@/components/referral/page-client'), {
  ssr: true,
});

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, req, resolvedUrl } = context;
  const path = resolvedUrl.split('?')[0];

  if (process.env.NEXT_PUBLIC_IS_OPEN_REFERRAL !== 'true') {
    return {
      redirect: {
        destination: `404`,
        permanent: false,
      },
    };
  }

  const [localeRes, tdkRes, dataRes] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(path, locale),
    fetchLoadingDataWith(path, locale),
  ]);

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const siteUrl = locale !== 'en' ? `https://${req.headers.host}/${locale}` : `https://${req.headers.host}`;

  const jsonLd = page3JsonLd(
    {
      title: tdkRes?.data?.title || '',
      desc: tdkRes?.data?.description ?? tempData?.head_Meta_Desc ?? null,
      url: canonicalUrl(req.headers.host, resolvedUrl, locale),
    },
    {
      title: 'Toolkit',
      url: siteUrl + '/toolkit',
    },
    {
      url: siteUrl,
    }
  );
  if (!dataRes?.data) {
    return {
      redirect: {
        destination: `/`,
        permanent: false,
      },
    };
  }
  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      locale,
      title: tdkRes?.data?.title ?? tempData?.head_Anonymous_Meta_Title ?? null,
      description: tdkRes?.data?.description ?? tempData?.head_Anonymously_Meta_Desc ?? null,
      contentData: dataRes?.data ?? null,
      jsonLd,
    },
  };
};

const PageContent: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (props) => {
  return <PageMainContent data={props} />;
};

PageContent.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default PageContent;
